import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import type { StageVelocity } from '@/types'
import { CATEGORICAL } from '@/lib/theme'

const colorMap = {
  green: { dot: CATEGORICAL.evergreen.solid, text: CATEGORICAL.evergreen.solid },
  yellow: { dot: CATEGORICAL.clay.solid, text: CATEGORICAL.clay.solid },
  red: { dot: CATEGORICAL.raspberry.solid, text: CATEGORICAL.raspberry.solid },
  neutral: { dot: null, text: null }, // Keep existing token classes for neutral
} as const

interface StageVelocityIndicatorProps {
  velocity: StageVelocity | undefined
  isLoading?: boolean
}

export function StageVelocityIndicator({ velocity, isLoading }: StageVelocityIndicatorProps) {
  if (isLoading) {
    return (
      <div className="text-xs text-muted-foreground" role="status">
        Loading velocity...
      </div>
    )
  }

  if (!velocity) return null

  const colors = colorMap[velocity.color]
  const label = velocity.avgDays
    ? `Day ${velocity.currentDays} of avg ${velocity.avgDays}`
    : `Day ${velocity.currentDays} in stage`

  return (
    <div className="flex items-center gap-2">
      <Popover>
        <PopoverTrigger asChild>
          <button
            className="flex items-center gap-1.5 text-xs hover:opacity-80 cursor-pointer rounded px-1.5 py-0.5 -ml-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={`${velocity.detail}. Click for stage averages`}
          >
            <span
              className={`inline-block h-2 w-2 rounded-full ${colors.dot ? '' : 'bg-muted-foreground'}`}
              style={colors.dot ? { backgroundColor: colors.dot } : undefined}
              aria-hidden="true"
            />
            <span
              className={`font-medium ${colors.text ? '' : 'text-muted-foreground'}`}
              style={colors.text ? { color: colors.text } : undefined}
            >
              {label}
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-3" align="start">
          <p className="text-xs font-semibold mb-2">Average Days per Stage (Won Deals)</p>
          <div className="space-y-1">
            {velocity.allStages.map((s) => {
              const isCurrent = s.stage === velocity.stageName
              return (
                <div
                  key={s.stage}
                  className={`flex items-center justify-between text-xs ${isCurrent ? 'font-semibold' : ''}`}
                >
                  <span className={isCurrent ? 'text-foreground' : 'text-muted-foreground'}>
                    {s.stage} {isCurrent ? '(current)' : ''}
                  </span>
                  <span className="text-muted-foreground">
                    {s.avgDays} days
                    <span className="text-[10px] ml-1">({s.sampleCount})</span>
                  </span>
                </div>
              )
            })}
          </div>
          {velocity.avgDays && (
            <p className="text-[10px] text-muted-foreground mt-2 border-t pt-1.5">
              You have been in {velocity.stageName} for {velocity.currentDays} day
              {velocity.currentDays !== 1 ? 's' : ''}. Won deals average {velocity.avgDays} days in
              this stage.
            </p>
          )}
        </PopoverContent>
      </Popover>
    </div>
  )
}
